import { mergeItemThreeWay } from './conflictEngine';
import { choosePreferredOperationalRecord, mergeOperationalRecordVersions, isOperationalCollection } from './operationalRecordUtils';
import { DB } from './deviceStorage';
// Utilitários centrais de merge/sync offline
const verKey = (farmId, col) => `fz:${farmId}:${col}:_ver`;
const shadowKey = (farmId, col) => `fz:${farmId}:${col}:_shadow`;
const cursorKey = (farmId, col) => `fz:${farmId}:${col}:_cursor`;
const deltaCursorKey = (farmId, col) => `fz:${farmId}:${col}:_delta_cursor`;
const opsKey = (farmId, col) => `fz:${farmId}:${col}:_oplog`;
const pendingKey = (farmId, col) => `fz:${farmId}:${col}:_pending`;

export const LAST_APP_VERSION_SYNCED_KEY = "fz:lastAppVersionSynced";

// v3.2.14: renomeado conceitualmente. Antes "on update" (automático),
// agora usado apenas pela ferramenta manual em Admin > Sincronização.
export const REVALIDATE_ON_UPDATE_COLLECTIONS = Object.freeze([
  "orders", "notifications", "services", "occurrences", "vet", "inspections"
]);

export function revalidateFlagKey(farmId, collection) {
  return `fz:sync:needsRevalidate:${farmId}:${collection}`;
}

export async function clearSyncCursor(farmId, collection) {
  try { await DB.del(cursorKey(farmId, collection)); } catch (_) {}
}

const APPEND_ONLY_COLLECTIONS = new Set(['services','occurrences','stockMovements','fueling','machineryMaintenance','vet']);
const NO_IMPLICIT_DELETE_COLLECTIONS = new Set(['orders']);
const CLIENT_KEYED_COLLECTIONS = new Set(['services','occurrences','orders','inspections','financial','vet','poi']);

function getPastureSyncBusinessKey(item) {
  if (!item || typeof item !== 'object') return '';
  const farmId = String(item?.farmId || '').trim();
  const name = String(item?.name || item?.title || item?.label || '').trim().toLowerCase();
  const costCenter = String(item?.costCenter || '').trim().toLowerCase();
  if (!farmId || !name) return '';
  return `pasture:biz:${farmId}:${name}:${costCenter}`;
}

function getSyncIdentityKey(item, collectionName='') {
  if (!item || typeof item !== 'object') return '';
  const col = String(collectionName || '').trim();
  const clientId = String(item?.clientId || '').trim();
  const id = String(item?.id || '').trim();
  if (col === 'pastures') {
    const biz = getPastureSyncBusinessKey(item);
    if (biz) return biz;
  }
  if (CLIENT_KEYED_COLLECTIONS.has(col) && clientId) return `client:${clientId}`;
  if (id) return `id:${id}`;
  if (clientId) return `client:${clientId}`;
  return '';
}

function isAppendOnlyCollection(collectionName='') {
  return APPEND_ONLY_COLLECTIONS.has(String(collectionName || '').trim());
}

function parseSyncTs(value) {
  if (!value) return 0;
  const n = Date.parse(value);
  return Number.isFinite(n) ? n : 0;
}

function itemSyncTs(item) {
  if (!item || typeof item !== "object") return 0;
  return Math.max(
    parseSyncTs(item.deletedAt),
    parseSyncTs(item.deleted_at),
    parseSyncTs(item.updatedAt),
    parseSyncTs(item.createdAt)
  );
}

function isDeletedItem(item) {
  return !!(item && typeof item === "object" && (item.deletedAt || item.deleted_at || item._deleted === true));
}

function stripDeletedItems(arr) {
  return (Array.isArray(arr) ? arr : []).filter(item => item && !isDeletedItem(item));
}

// v3.2.15: DEL pendente (pendingLocalDelete:true) não é tombstone confirmado.
// Ele tem deletedAt desde del(), mas deve permanecer visível até ACK do servidor.
function isConfirmedTombstone(item) {
  if (!item || typeof item !== "object") return false;
  if (item.pendingLocalDelete === true) return false;
  return !!(item.deletedAt || item.deleted_at || item._deleted === true || item.is_deleted === true);
}

// v3.2.15: para orders, preservar DEL pendente no visible até ACK.
// Para outras coleções, comportamento idêntico a stripDeletedItems.
function stripDeletedItemsForVisible(arr, collectionName) {
  if (collectionName !== "orders") return stripDeletedItems(arr);
  return (Array.isArray(arr) ? arr : []).filter(item =>
    item && !isConfirmedTombstone(item)
  );
}

function stableJson(value) {
  const seen = new WeakSet();
  const normalize = (input) => {
    if (input === null || typeof input !== "object") return input;
    if (seen.has(input)) return null;
    seen.add(input);
    if (Array.isArray(input)) return input.map(normalize);
    const out = {};
    Object.keys(input).sort().forEach((key) => {
      if (["updatedAt","updatedBy","createdAt","deletedAt","deleted_at","deletedBy","_deleted"].includes(key)) return;
      out[key] = normalize(input[key]);
    });
    return out;
  };
  return JSON.stringify(normalize(value));
}

function getChangedFields(baseItem, nextItem) {
  const keys = new Set([
    ...Object.keys(baseItem || {}),
    ...Object.keys(nextItem || {}),
  ]);
  const changed = {};
  for (const key of keys) {
    if (key === "id") continue;
    if (["updatedAt","updatedBy","createdAt","deletedAt","deleted_at","deletedBy","_deleted"].includes(key)) continue;
    if (stableJson(baseItem?.[key]) !== stableJson(nextItem?.[key])) {
      changed[key] = nextItem?.[key];
    }
  }
  return changed;
}


const PRIORITY_FIELDS = new Set([
  "status","priority","urgencia","urgency","responsavel","responsible","assignedTo",
  "assignee","colaboradorId","collaboratorId","scheduledDate","dataAgendada",
  "completedAt","doneAt","finishedAt"
]);

function isPriorityField(field) {
  return PRIORITY_FIELDS.has(String(field || "").trim());
}

function chooseFieldResolution({ field, baseVal, serverVal, localVal, localTs, serverTs }) {
  const baseJson = stableJson(baseVal);
  const serverJson = stableJson(serverVal);
  const localJson = stableJson(localVal);
  const localChanged = localJson !== baseJson;
  const serverChanged = serverJson !== baseJson;

  if (localChanged && !serverChanged) return { value: localVal, source: 'local', manualRequired: false };
  if (serverChanged && !localChanged) return { value: serverVal, source: 'server', manualRequired: false };
  if (!localChanged && !serverChanged) return { value: baseVal, source: 'base', manualRequired: false };
  if (localJson === serverJson) return { value: localVal, source: 'merged', manualRequired: false };

  const preferLocal = localTs >= serverTs;
  const preferredSource = preferLocal ? 'local' : 'server';
  const preferredValue = preferLocal ? localVal : serverVal;
  return {
    value: preferredValue,
    source: preferredSource,
    manualRequired: isPriorityField(field),
  };
}

function appendSyncHistory(previousRaw, nextRaw, actorName, source='sync') {
  const prev = Array.isArray(previousRaw) ? previousRaw : [];
  const next = Array.isArray(nextRaw) ? nextRaw : [];
  const prevById = new Map(prev.filter(Boolean).map(item => [String(item?.id), item]));
  return next.map((item) => {
    if (!item || typeof item !== 'object') return item;
    const prior = prevById.get(String(item?.id)) || null;
    const changedFields = Object.keys(getChangedFields(prior || {}, item || {}));
    const wasDeleted = isDeletedItem(prior);
    const isDeleted = isDeletedItem(item);
    const type = !prior ? 'created' : (isDeleted && !wasDeleted ? 'deleted' : (changedFields.length ? 'updated' : 'noop'));
    const existing = Array.isArray(item?._syncHistory) ? item._syncHistory : (Array.isArray(prior?._syncHistory) ? prior._syncHistory : []);
    if (type === 'noop') {
      return existing.length ? { ...item, _syncHistory: existing.slice(0, 12) } : item;
    }
    const entry = {
      at: item?.updatedAt || item?.deletedAt || new Date().toISOString(),
      by: item?.updatedBy || item?.deletedBy || actorName || 'system',
      source,
      type,
      changedFields,
    };
    return { ...item, _syncHistory: [entry, ...existing].slice(0, 12) };
  });
}

function buildOperationLog(baseRaw, localVisibleArr, userName, collectionName='') {
  const baseArr = Array.isArray(baseRaw) ? baseRaw : [];
  const localArr = Array.isArray(localVisibleArr) ? localVisibleArr : [];
  // v3.2.15: usar stripDeletedItemsForVisible para preservar DEL pendente
  // (pendingLocalDelete:true) no caminho de build do opLog de orders.
  const baseVisible = stripDeletedItemsForVisible(baseArr, collectionName);
  const localVisible = stripDeletedItemsForVisible(localArr, collectionName);
  const baseById = new Map(baseVisible.filter(Boolean).map(item => [getSyncIdentityKey(item, collectionName), item]).filter(([key]) => !!key));
  const localById = new Map(localVisible.filter(Boolean).map(item => [getSyncIdentityKey(item, collectionName), item]).filter(([key]) => !!key));
  const localRawById = new Map(localArr.filter(Boolean).map(item => [getSyncIdentityKey(item, collectionName), item]).filter(([key]) => !!key));
  const ids = new Set([...baseById.keys(), ...localById.keys(), ...localRawById.keys()]);
  const ops = [];
  const appendOnly = isAppendOnlyCollection(collectionName);
  const noImplicitDelete = NO_IMPLICIT_DELETE_COLLECTIONS.has(String(collectionName || '').trim());

  for (const id of ids) {
    const baseItem = baseById.get(id) || null;
    const localItem = localById.get(id) || null;
    const localRawItem = localRawById.get(id) || null;

    // v3.2.15: DEL pendente permanece visível, então não pode depender de
    // baseItem && !localItem para virar operação delete. Se o registro local
    // ainda está visível com pendingLocalDelete:true, gerar delete explícito.
    // v3.2.25: remover dependência de baseItem — o guard pode ter alterado
    // o shadow/baseRaw de forma que baseItem não existe, mas pendingLocalDelete
    // ou tombstonePending ainda exigem envio do tombstone ao servidor.
    if (localRawItem && (localRawItem.pendingLocalDelete === true || localRawItem.tombstonePending === true)) {
      const ts = localRawItem?.deletedAt || localRawItem?.deleted_at || localRawItem?.updatedAt || new Date().toISOString();
      const opId = localRawItem?.pendingOperationId || localRawItem?.localOperationId || localRawItem?.lastLocalOperationId || `del_${id}_${ts}`;
      ops.push({
        opId,
        type: "delete",
        itemId: id,
        ts,
        baseTs: baseItem ? itemSyncTs(baseItem) : 0,
        userName: userName || "system",
        payload: {
          ...(localRawItem || {}),
          id: localRawItem?.id || baseItem?.id || id,
          _deleted: true,
          deletedAt: localRawItem?.deletedAt || localRawItem?.deleted_at || ts,
          deleted_at: localRawItem?.deletedAt || localRawItem?.deleted_at || ts,
          deletedBy: localRawItem?.deletedBy || userName || "system",
          updatedAt: localRawItem?.updatedAt || ts,
          updatedBy: localRawItem?.updatedBy || userName || "system",
          pendingOperationId: opId,
          localOperationId: localRawItem?.localOperationId || opId,
          pendingOperationType: localRawItem?.pendingOperationType || "delete",
          pendingLocalDelete: true,
          tombstonePending: localRawItem?.tombstonePending === true ? true : undefined,
        },
      });
      continue;
    }

    // v3.2.24: PRV explícito. pendingLocalCreate:true gera create op mesmo que
    // o shadow já contenha o registro (guard pode ter injetado o PRV antes desta
    // chamada, fazendo baseItem existir e quebrando a detecção por !baseItem).
    if (localRawItem?.pendingLocalCreate === true && !localRawItem?.serverConfirmed) {
      const ts = localItem?.updatedAt || localItem?.createdAt || new Date().toISOString();
      ops.push({
        opId: localRawItem?.pendingOperationId || localRawItem?.localOperationId || localRawItem?.lastLocalOperationId || `op_${id}_${ts}_${Math.random().toString(36).slice(2,8)}`,
        type: "create",
        itemId: id,
        ts,
        baseTs: 0,
        userName: userName || "system",
        payload: localItem,
      });
      continue;
    }

    if (!baseItem && localItem) {
      const ts = localItem?.updatedAt || localItem?.createdAt || new Date().toISOString();
      ops.push({
        // v3.2.20: usar pendingOperationId do registro como opId para que o
        // ACK do servidor (que usa pendingOperationId) case com o opId no _oplog
        // e permita limpeza seletiva no Delta-then-push.
        opId: localItem?.pendingOperationId || localItem?.localOperationId || localItem?.lastLocalOperationId || `op_${id}_${ts}_${Math.random().toString(36).slice(2,8)}`,
        type: "create",
        itemId: id,
        ts,
        baseTs: 0,
        userName: userName || "system",
        payload: localItem,
      });
      continue;
    }
    if (baseItem && !localItem) {
      // v3.2.12: detectar DEL pendente (pendingLocalDelete:true) mesmo sem
      // _deleted:true local — _deleted:true só é aplicado ao payload enviado ao servidor.
      const explicitDelete = !!(localRawItem && (
        localRawItem._deleted === true ||
        localRawItem.deletedAt ||
        localRawItem.deleted_at ||
        localRawItem.pendingLocalDelete === true
      ));
      // v2.222.03: em Compras, ausência local não significa exclusão.
      // Um dispositivo atrasado pode não ter um pedido criado por outro; isso
      // não pode virar tombstone inferido. Só uma exclusão explícita continua
      // gerando operação delete.
      if ((appendOnly || noImplicitDelete) && !explicitDelete) continue;
      const ts = localRawItem?.deletedAt || localRawItem?.updatedAt || new Date().toISOString();
      const opId = localRawItem?.pendingOperationId || localRawItem?.localOperationId || localRawItem?.lastLocalOperationId || `op_${id}_${ts}_${Math.random().toString(36).slice(2,8)}`;
      ops.push({
        opId,
        type: "delete",
        itemId: id,
        ts,
        baseTs: itemSyncTs(baseItem),
        userName: userName || "system",
        payload: {
          ...(localRawItem || {}),
          id: localRawItem?.id || baseItem?.id || id,
          _deleted: true,
          deletedAt: localRawItem?.deletedAt || localRawItem?.deleted_at || ts,
          deleted_at: localRawItem?.deletedAt || localRawItem?.deleted_at || ts,
          deletedBy: localRawItem?.deletedBy || userName || "system",
          updatedAt: localRawItem?.updatedAt || ts,
          updatedBy: localRawItem?.updatedBy || userName || "system",
          // v3.2.12/v3.2.15: preservar para PHP gerar ACK de DEL com o mesmo operationId.
          pendingOperationId: localRawItem?.pendingOperationId || opId,
          localOperationId: localRawItem?.localOperationId || opId,
          pendingOperationType: localRawItem?.pendingOperationType || "delete",
          pendingLocalDelete: localRawItem?.pendingLocalDelete === true ? true : undefined,
          tombstonePending: localRawItem?.tombstonePending === true ? true : undefined,
        },
      });
      continue;
    }
    // v3.2.25: UDT explícito. pendingLocalUpdate/updatePending gera edit op
    // mesmo se o guard já injetou a versão UDT no shadow/baseRaw, tornando
    // baseItem e localItem iguais e quebrando a detecção por stableJson diff.
    if (
      localRawItem &&
      (localRawItem.pendingLocalUpdate === true || localRawItem.updatePending === true) &&
      !localRawItem.serverAckAt
    ) {
      const ts = localItem?.updatedAt || new Date().toISOString();
      ops.push({
        opId: localRawItem?.pendingOperationId || localRawItem?.localOperationId || localRawItem?.lastLocalOperationId || `op_${id}_${ts}_${Math.random().toString(36).slice(2,8)}`,
        type: "edit",
        itemId: id,
        ts,
        baseTs: baseItem ? itemSyncTs(baseItem) : 0,
        userName: userName || "system",
        payload: localItem,
      });
      continue;
    }

    if (baseItem && localItem && stableJson(baseItem) !== stableJson(localItem)) {
      const ts = localItem?.updatedAt || new Date().toISOString();
      ops.push({
        // v3.2.20: idem — usar pendingOperationId do registro.
        opId: localItem?.pendingOperationId || localItem?.localOperationId || localItem?.lastLocalOperationId || `op_${id}_${ts}_${Math.random().toString(36).slice(2,8)}`,
        type: "edit",
        itemId: id,
        ts,
        baseTs: itemSyncTs(baseItem),
        userName: userName || "system",
        changes: getChangedFields(baseItem, localItem),
        payload: localItem,
      });
    }
  }

  ops.sort((a,b)=> parseSyncTs(a?.ts) - parseSyncTs(b?.ts));
  return ops;
}

function applyOperationLog(serverRaw, operationLog, collectionName='') {
  const raw = Array.isArray(serverRaw) ? serverRaw.map(item => item && typeof item === "object" ? {...item} : item) : [];
  const byId = new Map(raw.filter(Boolean).map(item => [getSyncIdentityKey(item, collectionName), item]).filter(([key]) => !!key));
  const sortedOps = (Array.isArray(operationLog) ? operationLog : []).slice().sort((a,b)=> parseSyncTs(a?.ts) - parseSyncTs(b?.ts));
  const skipped = [];

  for (const op of sortedOps) {
    const opIdentity = String(op?.itemId || getSyncIdentityKey(op?.payload, collectionName) || `id:${String(op?.payload?.id || '')}`);
    if (!opIdentity) continue;
    const current = byId.get(opIdentity) || null;
    const payloadId = String(op?.payload?.id || current?.id || opIdentity.replace(/^(id:|client:)/, '') || '');
    const currentTs = itemSyncTs(current);
    const opTs = parseSyncTs(op?.ts);

    if (op?.type === "create") {
      if (!current) {
        byId.set(opIdentity, { ...(op.payload || {}), id: payloadId, updatedAt: op.ts || new Date().toISOString(), updatedBy: op.userName || "system" });
      } else if (opTs >= currentTs) {
        byId.set(opIdentity, { ...current, ...(op.payload || {}), id: payloadId, updatedAt: op.ts || current?.updatedAt || new Date().toISOString(), updatedBy: op.userName || current?.updatedBy || "system", _deleted: false, deletedAt: undefined, deleted_at: undefined, deletedBy: undefined });
      } else {
        skipped.push({ ...op, reason: "server_newer_than_create" });
      }
      continue;
    }

    if (op?.type === "edit") {
      if (!current) {
        if (op.payload) {
          byId.set(opIdentity, { ...op.payload, id: payloadId, updatedAt: op.ts || new Date().toISOString(), updatedBy: op.userName || "system" });
        }
        continue;
      }
      if (currentTs > opTs) {
        skipped.push({ ...op, reason: "server_newer_than_edit" });
        continue;
      }
      const next = { ...current, ...(op.changes || {}), ...(op.payload?.createdAt ? { createdAt: op.payload.createdAt } : {}), id: payloadId };
      delete next.deletedAt;
      delete next.deleted_at;
      delete next.deletedBy;
      next._deleted = false;
      next.updatedAt = op.ts || new Date().toISOString();
      next.updatedBy = op.userName || current?.updatedBy || "system";
      byId.set(opIdentity, next);
      continue;
    }

    if (op?.type === "delete") {
      if (!current) {
        byId.set(opIdentity, { ...(op.payload || {}), id: payloadId, _deleted: true, deletedAt: op.ts || new Date().toISOString(), deleted_at: op.ts || new Date().toISOString(), deletedBy: op.userName || "system", updatedAt: op.ts || new Date().toISOString(), updatedBy: op.userName || "system" });
        continue;
      }
      if (currentTs > opTs) {
        skipped.push({ ...op, reason: "server_newer_than_delete" });
        continue;
      }
      byId.set(opIdentity, {
        ...current,
        _deleted: true,
        deletedAt: op.ts || new Date().toISOString(),
        deleted_at: op.ts || new Date().toISOString(),
        deletedBy: op.userName || "system",
        updatedAt: op.ts || new Date().toISOString(),
        updatedBy: op.userName || "system",
      });
    }
  }

  const finalRaw = Array.from(byId.values()).sort((a, b) => {
    const an = Number(a?.id);
    const bn = Number(b?.id);
    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
    return String(a?.id).localeCompare(String(b?.id));
  });

  return { raw: finalRaw, visible: stripDeletedItemsForVisible(finalRaw, collectionName || ""), skipped };
}

function mergeFieldValue(baseVal, serverVal, localVal, preferLocal) {
  const resolution = chooseFieldResolution({
    field: '',
    baseVal,
    serverVal,
    localVal,
    localTs: preferLocal ? 1 : 0,
    serverTs: preferLocal ? 0 : 1,
  });
  return resolution.value;
}

function buildLocalRawForSync(baseRaw, localVisible, userName, collectionName='') {
  const baseArr = Array.isArray(baseRaw) ? baseRaw : [];
  const locArr = Array.isArray(localVisible) ? localVisible : [];
  const localById = new Map(locArr.filter(Boolean).map(item => [getSyncIdentityKey(item, collectionName), item]).filter(([key]) => !!key));
  const raw = [...locArr];
  const nowIso = new Date().toISOString();
  const appendOnly = isAppendOnlyCollection(collectionName);
  const noImplicitDelete = NO_IMPLICIT_DELETE_COLLECTIONS.has(String(collectionName || '').trim());
  for (const baseItem of baseArr) {
    const id = getSyncIdentityKey(baseItem, collectionName);
    if (!id || localById.has(id)) continue;
    if (isDeletedItem(baseItem) || appendOnly || noImplicitDelete) {
      // v2.222.03: para orders, não inferir exclusão quando o item está
      // no shadow/base mas não aparece no visible local. Preservar o item
      // confirmado até chegar tombstone/delete explícito.
      raw.push(baseItem);
      continue;
    }
    raw.push({
      ...baseItem,
      _deleted: true,
      deletedAt: nowIso,
      deleted_at: nowIso,
      deletedBy: userName || baseItem?.deletedBy || "system",
      updatedAt: nowIso,
      updatedBy: userName || baseItem?.updatedBy || "system",
    });
  }
  return raw;
}

function mergeCollectionsThreeWay({ baseArr, serverArr, localVisibleArr, userName, serverCollectionUpdatedAt, collectionName='' }) {
  const baseRaw = Array.isArray(baseArr) ? baseArr : [];
  const serverRaw = Array.isArray(serverArr) ? serverArr : [];
  const localRaw = buildLocalRawForSync(baseRaw, localVisibleArr, userName, collectionName);
  const ids = new Set([
    ...baseRaw.map(item => getSyncIdentityKey(item, collectionName)).filter(Boolean),
    ...serverRaw.map(item => getSyncIdentityKey(item, collectionName)).filter(Boolean),
    ...localRaw.map(item => getSyncIdentityKey(item, collectionName)).filter(Boolean),
  ]);
  const merged = [];
  const conflicts = [];

  for (const id of ids) {
    const baseItem = baseRaw.find(item => getSyncIdentityKey(item, collectionName) === id) || null;
    let serverItem = serverRaw.find(item => getSyncIdentityKey(item, collectionName) === id) || null;
    const localItem = localRaw.find(item => getSyncIdentityKey(item, collectionName) === id) || null;

    if (!serverItem && baseItem) {
      const localKeepsItem = !!(localItem && !isDeletedItem(localItem));
      if (!localKeepsItem) {
        if (isOperationalCollection(collectionName)) {
          // Coleções operacionais nunca recebem tombstone inferido por serverArr incompleto.
          // Preservar o item como se o servidor ainda o tivesse.
          // Deleções explícitas do usuário chegam via localItem e continuam prevalecendo.
          serverItem = { ...baseItem };
        } else {
          serverItem = {
            ...baseItem,
            _deleted: true,
            deletedAt: serverCollectionUpdatedAt || new Date().toISOString(),
            deleted_at: serverCollectionUpdatedAt || new Date().toISOString(),
            updatedAt: serverCollectionUpdatedAt || new Date().toISOString(),
            deletedBy: baseItem?.deletedBy || "server",
            updatedBy: baseItem?.updatedBy || "server",
          };
        }
      }
    }

    if (!baseItem && !serverItem && localItem) { merged.push(localItem); continue; }
    if (!baseItem && serverItem && !localItem) { merged.push(serverItem); continue; }
    if (baseItem && !serverItem && !localItem) continue;
    if (!baseItem && serverItem && localItem) {
      const preferLocal = itemSyncTs(localItem) > itemSyncTs(serverItem);
      merged.push(preferLocal ? localItem : serverItem);
      continue;
    }
    if (baseItem && !localItem && serverItem) {
      // v2.242.06: tombstone explícito no base/shadow vence servidor ativo.
      // Quando localItem está ausente por ter sido filtrado do visible após uma
      // exclusão explícita, não devemos ressuscitar o registro do servidor.
      if (isDeletedItem(baseItem)) { merged.push(baseItem); continue; }
      merged.push(serverItem); continue;
    }
    if (baseItem && localItem && !serverItem) { merged.push(localItem); continue; }

    if (isOperationalCollection(collectionName)) {
      merged.push(mergeOperationalRecordVersions([baseItem, serverItem, localItem].filter(Boolean)) || choosePreferredOperationalRecord(serverItem, localItem));
      continue;
    }

    const result = mergeItemThreeWay({
      collection: collectionName,
      id,
      baseItem,
      serverItem,
      localItem,
      userName,
    });
    merged.push(result.merged);

    if (Array.isArray(result.unresolved) && result.unresolved.length > 0) {
      const confidence = Math.max(0, 100 - (result.unresolved.length * 20) - (result.autoResolved?.length || 0) * 4);
      conflicts.push({
        id,
        localTs: itemSyncTs(localItem),
        serverTs: itemSyncTs(serverItem),
        local: localItem,
        server: serverItem,
        base: baseItem,
        fields: [...(result.autoResolved || []), ...(result.unresolved || [])],
        manualFields: result.unresolved,
        autoResolved: result.autoResolved || [],
        suggestedResolution: confidence >= 70 ? 'merged' : 'server',
        confidence: typeof result.confidence === 'number' ? result.confidence : confidence,
        severity: result.severity || 'medium',
        merged: result.merged,
      });
    }
  }

  const mergedWithHistory = appendSyncHistory(baseRaw, merged, userName || 'system', 'merge');
  mergedWithHistory.sort((a, b) => {
    const an = Number(a?.id);
    const bn = Number(b?.id);
    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
    return String(a?.id).localeCompare(String(b?.id));
  });

  return { raw: mergedWithHistory, visible: stripDeletedItemsForVisible(mergedWithHistory, collectionName || ""), conflicts };
}

// ─── SyncProvider — wraps the whole app ──────────────────────────────────────


export {
  verKey,
  shadowKey,
  cursorKey,
  deltaCursorKey,
  opsKey,
  pendingKey,
  parseSyncTs,
  itemSyncTs,
  isDeletedItem,
  stripDeletedItems,
  isConfirmedTombstone,
  stripDeletedItemsForVisible,
  stableJson,
  getChangedFields,
  buildOperationLog,
  applyOperationLog,
  mergeFieldValue,
  buildLocalRawForSync,
  mergeCollectionsThreeWay,
  appendSyncHistory,
  isAppendOnlyCollection,
};
